import { LitElement, html, nothing } from 'lit';
import { t } from '../i18n';
import { defineZenComponent } from './register';
import {
  isSettingsHash,
  resolveSettingsHash,
  settingsHash,
  settingsHistoryMode,
  visibleSections,
  type SettingsRole,
  type SettingsSection,
} from '../settings-nav-logic';
import {
  loadRuntimeCapabilities,
  subscribeRuntimeCapabilities,
  type RuntimeCapabilitySnapshot,
} from '../runtime-capabilities';
import {
  discardProfileChanges,
  discardProfileChangesForSettings,
  getProfileSnapshot,
  subscribeProfile,
  type ProfileSnapshot,
} from '../profile';
import { showToast } from '../toast';
import { currentUser } from '../state';
import type { ProfileSectionId } from './profile-sections';

const PROFILE_SECTION_IDS: readonly ProfileSectionId[] = ['profile', 'ai-pref', 'ai-work', 'advanced'];

export class ZenSettingsPage extends LitElement {
  static properties = {
    open: { attribute: false },
    activeSection: { attribute: false },
    drawerOpen: { attribute: false },
    profileSnapshot: { attribute: false },
    viewerRole: { attribute: false },
    workspaceAdminEnabled: { attribute: false },
    workspaceCapabilityLoading: { attribute: false },
    workspaceSyncRulesDirty: { attribute: false },
  };

  declare open: boolean;
  declare activeSection: string;
  declare drawerOpen: boolean;
  declare profileSnapshot: ProfileSnapshot;
  declare viewerRole: SettingsRole;
  declare workspaceAdminEnabled: boolean;
  declare workspaceCapabilityLoading: boolean;
  declare workspaceSyncRulesDirty: boolean;

  private unsubscribeProfile: (() => void) | null = null;
  private unsubscribeCapabilities: (() => void) | null = null;
  private mobileQuery: MediaQueryList | null = null;
  private lastSettingsUrl = '';
  private lastSettingsScrollTop = 0;
  private restoringSettingsLocation = false;

  constructor() {
    super();
    this.open = isSettingsHash(location.hash);
    this.viewerRole = 'member';
    this.workspaceAdminEnabled = false;
    this.workspaceCapabilityLoading = true;
    this.activeSection = 'profile';
    this.drawerOpen = false;
    this.profileSnapshot = getProfileSnapshot();
    this.workspaceSyncRulesDirty = false;
    if (this.open) this.lastSettingsUrl = location.href;
  }

  createRenderRoot() {
    return this;
  }

  connectedCallback() {
    super.connectedCallback();
    this.unsubscribeProfile = subscribeProfile((snapshot) => {
      this.profileSnapshot = snapshot;
    });
    window.addEventListener('hashchange', this.onHashChange);
    window.addEventListener('beforeunload', this.onBeforeUnload);
    document.addEventListener('zen-lang-change', this.onLangChange);
    document.addEventListener('keydown', this.onKeyDown);
    document.addEventListener('click', this.onDocumentClickCapture, true);
    this.addEventListener('workspace-settings-dirty-change', this.onWorkspaceSettingsDirtyChange);
    document.addEventListener('workspace-settings-discard', this.onWorkspaceSettingsDiscard);
    this.mobileQuery = window.matchMedia('(max-width: 860px)');
    this.mobileQuery.addEventListener('change', this.onBreakpointChange);
    this.syncBodyState();
    this.unsubscribeCapabilities = subscribeRuntimeCapabilities(this.onCapabilitiesChanged);
    void loadRuntimeCapabilities();
  }

  disconnectedCallback() {
    this.unsubscribeProfile?.();
    this.unsubscribeProfile = null;
    this.unsubscribeCapabilities?.();
    this.unsubscribeCapabilities = null;
    window.removeEventListener('hashchange', this.onHashChange);
    window.removeEventListener('beforeunload', this.onBeforeUnload);
    document.removeEventListener('zen-lang-change', this.onLangChange);
    document.removeEventListener('keydown', this.onKeyDown);
    document.removeEventListener('click', this.onDocumentClickCapture, true);
    this.removeEventListener('workspace-settings-dirty-change', this.onWorkspaceSettingsDirtyChange);
    document.removeEventListener('workspace-settings-discard', this.onWorkspaceSettingsDiscard);
    this.mobileQuery?.removeEventListener('change', this.onBreakpointChange);
    this.mobileQuery = null;
    document.body.classList.remove('settings-open');
  }

  protected updated(changed: Map<PropertyKey, unknown>) {
    if (changed.has('open') && this.open) requestAnimationFrame(() => this.setupOpenShell());
    this.syncDrawerDialog();
  }

  private syncBodyState() {
    document.body.classList.toggle('settings-open', this.open);
  }

  private onLangChange = () => this.requestUpdate();

  private onBeforeUnload = (event: BeforeUnloadEvent) => {
    if (!this.open || !this.hasUnsavedChanges()) return;
    event.preventDefault();
    event.returnValue = '';
  };

  private confirmDiscard(): boolean {
    if (!this.hasUnsavedChanges()) return true;
    const key = this.profileSnapshot.isDirty
      ? 'settingsUnsavedExitConfirm'
      : 'settingsUnsavedWorkspaceExitConfirm';
    return window.confirm(t(key) as string);
  }

  private hasUnsavedChanges(): boolean {
    return this.profileSnapshot.isDirty || this.workspaceSyncRulesDirty;
  }

  private onWorkspaceSettingsDirtyChange = (event: Event) => {
    const detail = (event as CustomEvent<{ dirty?: boolean }>).detail;
    this.workspaceSyncRulesDirty = detail?.dirty === true;
  };

  private onWorkspaceSettingsDiscard = () => {
    this.workspaceSyncRulesDirty = false;
  };

  private discardWorkspaceSettingsChanges() {
    document.dispatchEvent(new CustomEvent('workspace-settings-discard'));
  }

  private onDocumentClickCapture = (event: Event) => {
    if (!this.open || !this.hasUnsavedChanges()) return;
    const target = event.target;
    if (!(target instanceof Element) || !target.closest('#appHeaderBrand')) return;
    if (this.confirmDiscard()) {
      discardProfileChanges();
      this.discardWorkspaceSettingsChanges();
      return;
    }
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  private onKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.open) return;
    const drawer = this.querySelector<HTMLDialogElement>('.settings-mobile-drawer');
    if (this.escapeBelongsToChildModal(event, drawer)) return;
    if (drawer?.open) {
      event.preventDefault();
      this.closeDrawer();
      return;
    }
    event.preventDefault();
    this.requestSettingsExit();
  };

  private escapeBelongsToChildModal(event: KeyboardEvent, drawer: HTMLDialogElement | null): boolean {
    const isVisibleModal = (element: Element): boolean => {
      if (element === drawer) return false;
      const style = getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
    };
    const pathModal = event.composedPath().find((target) =>
      target instanceof Element
      && target.matches('dialog[open], [role="dialog"], [role="alertdialog"]')
      && isVisibleModal(target),
    );
    if (pathModal) return true;
    return Array.from(document.querySelectorAll('dialog[open], [role="dialog"], [role="alertdialog"]')).some(isVisibleModal);
  }

  private onBreakpointChange = (event: MediaQueryListEvent) => {
    if (!event.matches && this.drawerOpen) this.closeDrawer(false);
  };

  private onHashChange = () => {
    if (this.restoringSettingsLocation) {
      this.restoringSettingsLocation = false;
      return;
    }

    const nextIsSettings = isSettingsHash(location.hash);
    if (this.open && !nextIsSettings) {
      // After logout there is nothing to save the edits to, so do not offer to stay.
      if (currentUser && !this.confirmDiscard()) {
        this.restoringSettingsLocation = true;
        history.pushState('', document.title, this.lastSettingsUrl);
        queueMicrotask(() => { this.restoringSettingsLocation = false; });
        this.open = true;
        this.syncBodyState();
        requestAnimationFrame(() => {
          const page = this.querySelector<HTMLElement>('.settings-page');
          if (page) page.scrollTop = this.lastSettingsScrollTop;
        });
        return;
      }
      discardProfileChanges();
      this.discardWorkspaceSettingsChanges();
      this.open = false;
      this.closeDrawer(false);
      this.syncBodyState();
      return;
    }

    this.open = nextIsSettings;
    this.syncBodyState();
    if (!nextIsSettings) return;
    // Keep a requested workspace deep link intact while the explicit config
    // capability is unresolved. The settled callback decides whether to keep
    // or safely normalize it.
    if (this.workspaceCapabilityLoading) {
      this.lastSettingsUrl = location.href;
      return;
    }
    const ids = this.sections.map((section) => section.id);
    const sectionId = resolveSettingsHash(location.hash, ids);
    const canonicalHash = settingsHash(sectionId);
    if (location.hash !== canonicalHash) history.replaceState('', document.title, canonicalHash);
    this.lastSettingsUrl = location.href;
    if (this.activeSection !== sectionId) this.showSection(sectionId, false);
  };

  private requestSettingsExit() {
    if (!this.confirmDiscard()) return;
    discardProfileChanges();
    this.discardWorkspaceSettingsChanges();
    history.pushState('', document.title, location.pathname + location.search);
    window.dispatchEvent(new Event('hashchange'));
  }

  private get sections(): SettingsSection[] {
    return visibleSections(this.viewerRole, this.workspaceAdminEnabled);
  }

  private onCapabilitiesChanged = (snapshot: RuntimeCapabilitySnapshot) => {
    this.workspaceCapabilityLoading = snapshot.status !== 'ready';
    this.workspaceAdminEnabled = snapshot.status === 'ready' && snapshot.capabilities.workspace_admin === true;
    if (this.workspaceCapabilityLoading) {
      this.requestUpdate();
      return;
    }

    const ids = this.sections.map((section) => section.id);
    if (this.open && isSettingsHash(location.hash)) {
      const sectionId = resolveSettingsHash(location.hash, ids);
      const canonicalHash = settingsHash(sectionId);
      if (location.hash !== canonicalHash) history.replaceState('', document.title, canonicalHash);
      this.activeSection = sectionId;
      this.lastSettingsUrl = location.href;
    } else if (!ids.includes(this.activeSection)) {
      this.activeSection = ids[0] ?? 'profile';
    }
    if (this.open) requestAnimationFrame(() => this.setupOpenShell());
    this.requestUpdate();
  }

  private setupOpenShell() {
    if (!this.open || this.workspaceCapabilityLoading) return;
    const ids = this.sections.map((section) => section.id);
    const sectionId = resolveSettingsHash(location.hash, ids);
    const canonicalHash = settingsHash(sectionId);
    if (location.hash !== canonicalHash) history.replaceState('', document.title, canonicalHash);
    this.activeSection = sectionId;
    this.lastSettingsUrl = location.href;
  }

  private onPageScroll = (event: Event) => {
    this.lastSettingsScrollTop = (event.currentTarget as HTMLElement).scrollTop;
  };

  // #2749: one setting per screen. The menu switches the screen instead of
  // scrolling a long page, so each setting reads on its own.
  private navigateToSection(event: Event, sectionId: string, fromDrawer: boolean) {
    event.preventDefault();
    const nextHash = settingsHash(sectionId);
    if (settingsHistoryMode(location.hash, nextHash) === 'replace') {
      history.replaceState('', document.title, nextHash);
    }
    this.lastSettingsUrl = location.href;
    if (fromDrawer) this.closeDrawer(false);
    this.showSection(sectionId, true);
  }

  private showSection(sectionId: string, focusHeading: boolean) {
    this.activeSection = sectionId;
    void this.updateComplete.then(() => {
      const page = this.querySelector<HTMLElement>('.settings-page');
      if (page) page.scrollTop = 0;
      window.scrollTo({ top: 0 });
      if (focusHeading) this.querySelector<HTMLElement>(`#settings-${sectionId} h3`)?.focus({ preventScroll: true });
    });
  }

  private openDrawer() {
    this.drawerOpen = true;
  }

  private closeDrawer(returnFocus = true) {
    this.drawerOpen = false;
    const page = this.querySelector<HTMLElement>('.settings-page');
    page?.classList.remove('drawer-open');
    if (returnFocus) requestAnimationFrame(() => this.querySelector<HTMLElement>('.settings-mobile-menu-button')?.focus());
  }

  private syncDrawerDialog() {
    const dialog = this.querySelector<HTMLDialogElement>('.settings-mobile-drawer');
    const page = this.querySelector<HTMLElement>('.settings-page');
    if (!dialog || !page) return;
    if (this.drawerOpen && !dialog.open && this.mobileQuery?.matches) {
      dialog.showModal();
      page.classList.add('drawer-open');
      requestAnimationFrame(() => dialog.querySelector<HTMLElement>('.nav-item.active')?.focus());
    } else if ((!this.drawerOpen || !this.mobileQuery?.matches) && dialog.open) {
      dialog.close();
      page.classList.remove('drawer-open');
    }
  }

  private onDrawerCancel = (event: Event) => {
    event.preventDefault();
    this.closeDrawer();
  };

  private onDrawerBackdropClick = (event: Event) => {
    if (event.target === event.currentTarget) this.closeDrawer();
  };

  private renderOwnBadge(group: SettingsSection['group']) {
    if (group === 'workspace') {
      return html`<span class="own-badge ws" title=${t('settingsBadgeWorkspaceSharedHint')}><span class="mi">group</span>${t('settingsBadgeWorkspaceShared')}</span>`;
    }
    return html`<span class="own-badge" title=${t('settingsBadgePersonalHint')}><span class="mi">lock</span>${t('settingsBadgePersonal')}</span>`;
  }

  private renderNavGroup(groupLabel: string, groupIcon: string, sections: SettingsSection[], extraClass: string, inDrawer = false) {
    if (sections.length === 0) return nothing;
    return html`
      <div class="nav-group ${extraClass}">
        <div class="nav-group-label"><span class="mi">${groupIcon}</span>${groupLabel}</div>
        <nav aria-label=${groupLabel}>
          ${sections.map((section) => html`
            <a
              class="nav-item ${this.activeSection === section.id ? 'active' : ''}"
              href=${settingsHash(section.id)}
              aria-current=${this.activeSection === section.id ? 'page' : nothing}
              @click=${(event: Event) => this.navigateToSection(event, section.id, inDrawer)}
            >
              <span class="mi">${section.icon}</span>${t(section.labelKey)}
            </a>
          `)}
        </nav>
      </div>
    `;
  }

  private renderSectionBody(section: SettingsSection) {
    if (PROFILE_SECTION_IDS.includes(section.id as ProfileSectionId)) {
      return html`<zen-profile-sections
        sections=${section.id}
        source="settings"
        .formData=${this.profileSnapshot.current}
        .state=${{
          loading: this.profileSnapshot.loading,
          saving: this.profileSnapshot.isSaving,
          error: '',
          success: '',
          timezoneOptions: this.profileSnapshot.timezoneOptions,
        }}
      ></zen-profile-sections>`;
    }
    if (section.id === 'my-connections') {
      return html`<zen-my-connections-section></zen-my-connections-section><zen-account-security-section></zen-account-security-section>`;
    }
    if (section.id === 'style') return html`<zen-style-profile-section></zen-style-profile-section>`;
    if (section.id === 'integration-policy') return html`<zen-integration-policy-section></zen-integration-policy-section>`;
    if (section.id === 'members') return html`<zen-workspace-panel embedded></zen-workspace-panel>`;
    if (section.id === 'sync-rules') return html`<zen-workspace-sync-rules-section></zen-workspace-sync-rules-section>`;
    return html`<div class="settings-section-placeholder">${t('settingsSectionPlaceholder')}</div>`;
  }

  private renderSection(section: SettingsSection) {
    return html`
      <section class="card settings-card" id="settings-${section.id}">
        <div class="card-head">
          <h3 tabindex="-1"><span class="mi">${section.icon}</span>${t(section.labelKey)}</h3>
          ${this.renderOwnBadge(section.group)}
        </div>
        <div class="card-body">${this.renderSectionBody(section)}</div>
      </section>
    `;
  }

  private renderGroupHeader(label: string, icon: string, note: string, extraClass: string) {
    return html`
      <div class="group-header ${extraClass}">
        <h2><span class="mi">${icon}</span>${label}</h2>
        <div class="group-note"><span class="mi">${extraClass === 'ws' ? 'visibility' : 'lock'}</span><span>${note}</span></div>
      </div>
    `;
  }

  private discardSettingsChanges = async () => {
    const page = this.querySelector<HTMLElement>('.settings-page');
    const scrollTop = page?.scrollTop ?? 0;
    const sectionId = this.activeSection;
    const discarded = await discardProfileChangesForSettings();
    if (!discarded) return;

    showToast(t('settingsChangesDiscarded') as string, 'info');
    requestAnimationFrame(() => {
      const currentPage = this.querySelector<HTMLElement>('.settings-page');
      if (!currentPage) return;
      const previousScrollBehavior = currentPage.style.scrollBehavior;
      currentPage.style.scrollBehavior = 'auto';
      requestAnimationFrame(() => {
        this.querySelector<HTMLElement>(`#settings-${sectionId} h3`)?.focus({ preventScroll: true });
        currentPage.scrollTop = scrollTop;
        requestAnimationFrame(() => {
          currentPage.style.scrollBehavior = previousScrollBehavior;
        });
      });
    });
  };

  private renderSaveTray() {
    const snapshot = this.profileSnapshot;
    if (!snapshot.isDirty && !snapshot.isSaving && snapshot.result.status === 'idle') return nothing;
    return html`
      <div class="settings-save-tray" role="region" aria-label=${t('settingsSaveRegionLabel')}>
        <div class="settings-save-copy">
          <strong>${snapshot.isDirty ? t('settingsUnsavedChanges') : t('settingsProfileSaved')}</strong>
          <span>${t('settingsSaveScope')}</span>
          <span class="settings-save-result ${snapshot.result.status}" aria-live="polite" aria-atomic="true">${snapshot.result.message}</span>
        </div>
        <div class="settings-save-actions">
          <button
            type="button"
            class="btn btn-secondary"
            ?disabled=${snapshot.isSaving || snapshot.loading || !snapshot.isDirty}
            @click=${this.discardSettingsChanges}
          >
            ${t('settingsDiscardChanges')}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            ?disabled=${snapshot.isSaving || snapshot.loading || !snapshot.isDirty}
            @click=${() => this.dispatchEvent(new CustomEvent('profile-save', { bubbles: true, composed: true, detail: { source: 'settings' } }))}
          >
            ${snapshot.isSaving ? html`<span class="spinner spinner-white"></span>${t('settingsSaving')}` : t('settingsSaveProfileChanges')}
          </button>
        </div>
      </div>
    `;
  }

  render() {
    if (!this.open) return nothing;
    const sections = this.sections;
    const youSections = sections.filter((section) => section.group === 'you');
    const workspaceSections = sections.filter((section) => section.group === 'workspace');
    const active = sections.find((section) => section.id === this.activeSection) || sections[0];

    return html`
      <div class="settings-page" @scroll=${this.onPageScroll}>
        <div class="settings-mobile-header">
          <div><span>${t('settingsTitle')}</span><strong>${active ? t(active.labelKey) : ''}</strong></div>
          <button
            type="button"
            class="btn btn-secondary settings-mobile-menu-button"
            aria-haspopup="dialog"
            aria-controls="settingsMobileDrawer"
            aria-expanded=${this.drawerOpen ? 'true' : 'false'}
            @click=${this.openDrawer}
          >
            <span class="mi mi-sm">menu</span>${t('settingsMenuOpen')}
          </button>
        </div>
        <dialog
          id="settingsMobileDrawer"
          class="settings-mobile-drawer"
          aria-labelledby="settingsMobileDrawerTitle"
          aria-modal="true"
          @cancel=${this.onDrawerCancel}
          @click=${this.onDrawerBackdropClick}
        >
          <div class="settings-mobile-drawer-panel">
            <div class="settings-mobile-drawer-head">
              <h2 id="settingsMobileDrawerTitle">${t('settingsMenuTitle')}</h2>
              <button type="button" class="modal-close" aria-label=${t('closeLabel')} @click=${() => this.closeDrawer()}><span class="mi">close</span></button>
            </div>
            ${this.renderNavGroup(t('settingsGroupYou') as string, 'person', youSections, 'you', true)}
            ${this.renderNavGroup(t('settingsGroupWorkspace') as string, 'group', workspaceSections, 'ws', true)}
          </div>
        </dialog>
        <div class="settings-shell">
          <aside class="sidenav">
            <div class="settings-sidenav-head"><h1>${t('settingsTitle')}</h1></div>
            ${this.renderNavGroup(t('settingsGroupYou') as string, 'person', youSections, 'you')}
            ${this.renderNavGroup(t('settingsGroupWorkspace') as string, 'group', workspaceSections, 'ws')}
          </aside>
          <main class="content">
            ${active ? html`
              ${active.group === 'workspace'
                ? this.renderGroupHeader(t('settingsGroupWorkspace') as string, 'group', t('settingsWorkspaceGroupNote') as string, 'ws')
                : this.renderGroupHeader(t('settingsGroupYou') as string, 'person', t('settingsYouGroupNote') as string, 'you')}
              ${this.renderSection(active)}
            ` : nothing}
            ${this.renderSaveTray()}
          </main>
        </div>
      </div>
    `;
  }
}

defineZenComponent('zen-settings-page', ZenSettingsPage);
